import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";

// Assets
import empireLogo from "../../assets/portfolio/coming-soon.png";
import empirePreview1 from "../../assets/portfolio/empire-1.jpeg";
import empirePreview2 from "../../assets/portfolio/empire-2.jpeg";
import empirePreview3 from "../../assets/portfolio/empire-3.jpeg";
import empirePreview4 from "../../assets/portfolio/empire-4.jpeg";
import empirePreview5 from "../../assets/portfolio/empire-5.jpeg";
import empirePreview6 from "../../assets/portfolio/empire-6.jpeg";
// Tech icons
import { FaReact, FaAws } from "react-icons/fa";
import { SiTailwindcss, SiMongodb } from "react-icons/si";

/* ================= AUTO SWIPE PREVIEW ================= */
function AutoSwipePreviews({ previews }) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % previews.length);
    }, 4000);

    return () => clearInterval(timer);
  }, [previews.length]);

  return (
    <div className="relative w-full overflow-hidden">
      <div className="flex justify-center">
        <AnimatePresence mode="wait">
          <motion.img
            key={index}
            src={previews[index]}
            alt="Empire Dashboard Preview"
            initial={{ x: 120, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -120, opacity: 0 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
            className="
              w-[720px] md:w-[900px]
              rounded-3xl
              border border-black/10
              shadow-2xl
            "
          />
        </AnimatePresence>
      </div>
    </div>
  );
}

/* ================= MAIN PAGE ================= */
export default function EmpirePage() {
  const previews = [
    empirePreview1,
    empirePreview2,
    empirePreview3,
    empirePreview4,
    empirePreview5,
    empirePreview6
  ];

  return (
    <div className="w-full bg-transparent text-black pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-20">

        {/* ================= HERO ================= */}
        <section className="flex flex-col items-center gap-10 text-center">

          {/* TEXT */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="hero-heading mb-4">
              Empire Dashboard
            </h1>

            <p className="hero-copy">
              A centralized operations dashboard built to give businesses
              complete visibility and control over data, workflows, and decisions.
            </p>
          </motion.div>

          {/* LOGO */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex justify-center"
          >
            <img
              src={empireLogo}
              alt="Empire Dashboard"
              className="
                w-64 h-64 object-contain
                drop-shadow-[0_25px_35px_rgba(0,0,0,0.15)]
              "
            />
          </motion.div>

        </section>

        {/* ================= PROJECT OVERVIEW ================= */}
        <section className="mt-32">
          <div className="mx-auto max-w-5xl text-center">
            <motion.h2
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="section-heading mb-6"
            >
              Project Overview
            </motion.h2>

            <p className="section-copy">
              Empire Dashboard was built to solve a common problem faced by
              growing businesses: scattered data, disconnected tools, and
              limited visibility across operations. The objective was to create
              a single system that brings everything together in one place.
            </p>

            <p className="section-copy mt-6">
              The product focuses on clarity, speed, and structure, enabling
              teams and decision makers to monitor performance, track activity,
              and act quickly using real time insights.
            </p>
          </div>
        </section>

        {/* ================= DASHBOARD PREVIEWS ================= */}
        <section className="mt-24">
          <AutoSwipePreviews previews={previews} />
        </section>

        {/* ================= OUR STRATEGY ================= */}
        <section className="mx-auto mt-32 max-w-5xl text-center">
          <h2 className="section-heading mb-6">
            Our Strategy
          </h2>

          <p className="section-copy">
            We approached Empire as a system, not just a dashboard. The strategy
            focused on structuring data clearly, reducing cognitive load, and
            ensuring that every screen answers a real operational question.
          </p>

          <p className="section-copy mt-6">
            The interface was designed to scale with the business — allowing new
            modules, roles, and features to be added without disrupting existing
            workflows.
          </p>
        </section>

        {/* ================= WHAT WE BUILT ================= */}
        <section className="mt-32">
          <h2 className="section-heading mb-10">
            What We Built
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              "Centralized operations dashboard",
              "Role-based access & views",
              "Real-time data visualization",
              "Modular and scalable architecture",
              "Performance-focused UI",
              "Business-ready analytics foundation",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="
                  bg-white border border-black/10 rounded-xl
                  p-6 shadow-sm hover:shadow-xl transition
                "
              >
                <h3 className="text-[15px] font-medium">{item}</h3>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ================= TECH STACK ================= */}
        <section className="mt-32">
          <h2 className="section-heading mb-10">
            Tech Stack
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {[
              { name: "React", icon: <FaReact /> },
              { name: "Tailwind CSS", icon: <SiTailwindcss /> },
              { name: "AWS Server", icon: <FaAws /> },
              { name: "MongoDB", icon: <SiMongodb /> },
            ].map((t, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="
                  flex flex-col items-center justify-center
                  border border-black/10 rounded-xl
                  p-6 shadow-sm hover:shadow-xl transition
                  group
                "
              >
                <div className="text-3xl mb-3 text-black/80 group-hover:scale-110 transition">
                  {t.icon}
                </div>
                <span className="text-[14px] font-medium">{t.name}</span>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ================= CTA ================= */}
        <section className="mt-32 text-center">
          <h2 className="section-heading mb-6">
            Need a Dashboard Like This?
          </h2>

          <p className="mx-auto mb-8 max-w-xl text-center text-neutral-600">
            We design and build internal tools, dashboards, and systems
            tailored to real business workflows.
          </p>

          <a
            href="/contact"
            className="
              inline-block px-10 py-3 rounded-full
              bg-black text-white font-medium
              hover:bg-opacity-80 transition
            "
          >
            Start Your Project
          </a>
        </section>

      </div>
    </div>
  );
}
